

import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import PropTypes from "prop-types";

import AppBar from "@mui/material/AppBar";
import {
  Avatar, 
  Box, 
  Tooltip,  
  Alert, 
  Snackbar,
  Divider,
  ButtonGroup,
  Typography,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Skeleton from "@mui/material/Skeleton";
import Badge from "@mui/material/Badge";
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { styled, alpha } from "@mui/material/styles";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';

import MDBox from "components/MDBox";
import { formatDistanceToNow, format } from 'date-fns';
import { parseServerDateTime } from 'utils/DateTimeConversion';
import { getNotificationRedirectPath } from 'utils/notificationRedirect';
import * as whatsappNotifySound from 'whatsapp/notifySound';
import Breadcrumbs from "examples/Breadcrumbs";
// import DarkModeToggle from "components/CustomDarkMode";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setTransparentNavbar,
} from "context";
import SearchComponent from "./components/SearchComponent";
import { useWebSocket } from "context/WebSocketContext";
import MDUserAvatar from "components/MDUserAvatar";
import { APP_SCALE } from "assets/theme/base/appScale";
import useUiVersion from "hooks/useUiVersion";
import { UI_LEGACY, UI_V2 } from "hooks/uiVersion";
import PageBreadcrumbs from "shell-v2/PageBreadcrumbs";


// Add these styled components
const NotificationTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '4px',
}));

const NotificationMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginTop: '4px',
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const NotificationAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  backgroundColor: theme.palette.primary.main,
}));

const StyledNotificationText = styled(Typography)(({ theme }) => ({
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  maxWidth: '100%',
  lineHeight: 1.6,
  fontSize: '0.875rem',
}));

// Update the NotificationHeader component
const NotificationHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  background: theme.palette.background.default,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backdropFilter: 'blur(8px)',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '16px 20px',
  transition: 'all 0.2s ease',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 5px rgba(0,0,0,0.05)',
  },
}));

// Add ZoomControls component
const ZOOM_STORAGE_KEY = 'pearl.zoom';
const DEFAULT_ZOOM = APP_SCALE * 100;

const readStoredZoom = () => {
  try {
    const stored = window.localStorage.getItem(ZOOM_STORAGE_KEY);
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private browsing / storage disabled — fall back to the CSS default.
    return null;
  }
};

const ZoomControls = () => {
  // null means "no user override" — the stylesheet's default scale applies.
  const [zoom, setZoom] = useState(readStoredZoom);

  // Re-apply a stored override on mount. Nothing is written when there is no
  // override, which is what lets the stylesheet keep scaling by breakpoint:
  // an inline style would apply at every width, including mobile.
  useEffect(() => {
    if (zoom !== null) {
      document.body.style.zoom = `${zoom}%`;
    }
    // Intentionally mount-only: later changes are applied by handleZoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyZoom = (newZoom) => {
    setZoom(newZoom);
    document.body.style.zoom = `${newZoom}%`;
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, String(newZoom));
    } catch {
      // Persisting is best-effort.
    }
  };

  const handleZoom = (action) => {
    const current = zoom ?? DEFAULT_ZOOM;
    switch (action) {
      case 'in':
        applyZoom(Math.min(current + 10, 200));
        break;
      case 'out':
        applyZoom(Math.max(current - 10, 50));
        break;
      case 'reset':
        // Drop the override entirely and hand control back to the stylesheet.
        setZoom(null);
        document.body.style.removeProperty('zoom');
        try {
          window.localStorage.removeItem(ZOOM_STORAGE_KEY);
        } catch {
          // Best-effort.
        }
        break;
      default:
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title="Zoom out">
        <IconButton
          size="small"
          disableRipple
          onClick={() => handleZoom('out')}
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.main : theme.palette.text.secondary,
            height: 32,
            width: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography 
            variant="button" 
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 'bold',
              letterSpacing: -0.5,
            }}
          >
            A
          </Typography>
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Zoom in">
        <IconButton
          size="small"
          disableRipple
          onClick={() => handleZoom('in')}
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.main : theme.palette.text.secondary,
            height: 32,
            width: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography 
            variant="button" 
            sx={{ 
              fontSize: '1rem',
              fontWeight: 'bold',
              letterSpacing: -0.5,
            }}
          >
            A
          </Typography>
        </IconButton>
      </Tooltip>

      <Tooltip title="Reset zoom">
        <IconButton
          size="small"
          disableRipple
          onClick={() => handleZoom('reset')}
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.main : theme.palette.text.secondary,
            height: 32,
            width: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

function ClassicDashboardNavbar({ absolute, light, isMini }) {
  // const theme = useTheme();
  const { notifications, setNotifications, whatsappInboxEvent, wsConnected } = useWebSocket();
  const navigate = useNavigate();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const { user, axiosInstance, signout, token } = useAuth();
  const { isV2, enabled: uiV2Enabled, setUiVersion } = useUiVersion();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const whatsappEnabled = !entitlementsLoading && isModuleEnabled("WHATSAPP");
  const [avatarMenu, setAvatarMenu] = useState(null);
  const [messageUnread, setMessageUnread] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Snackbar handlers
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };


  const fetchNotificationsViaREST = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/notifications/`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showNotification('Failed to fetch notifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Initial notifications fetch
  useEffect(() => {
    if (user?.id && token) {
      fetchNotificationsViaREST();
    }
  }, [user?.id, token]);

  const fetchMessageUnread = useCallback(async () => {
    if (!user?.id || !token || !whatsappEnabled) {
      setMessageUnread(0);
      return 0;
    }
    try {
      const response = await axiosInstance.get('/whatsapp/inbox/unread-count');
      const count = Number(response.data?.count) || 0;
      setMessageUnread(count);
      return count;
    } catch (error) {
      // No seat / not configured — clear the badge. Do not chime on WS events either;
      // queue membership without a WHATSAPP seat always 403s here (seats_total may be 0).
      setMessageUnread(0);
      return 0;
    }
  }, [axiosInstance, token, user?.id, whatsappEnabled]);

  useEffect(() => {
    if (!whatsappEnabled) {
      setMessageUnread(0);
      return undefined;
    }
    fetchMessageUnread();
    // Live updates come from whatsapp_inbox_updated over WS; poll faster when
    // the socket is down so the badge still moves without a manual refresh.
    const pollMs = wsConnected ? 120000 : 15000;
    const interval = setInterval(fetchMessageUnread, pollMs);
    return () => clearInterval(interval);
  }, [fetchMessageUnread, whatsappEnabled, wsConnected]);

  // Unlock audio on first click/key so inbound WhatsApp chimes are not blocked.
  useEffect(() => {
    if (!whatsappEnabled) return undefined;
    whatsappNotifySound.bindAutoPrime();
    return undefined;
  }, [whatsappEnabled]);

  // Instant red-dot + chime. Schema-wide WS events used to ring every open tab;
  // only chime when this agent's unread count actually increases.
  useEffect(() => {
    if (!whatsappEnabled || !whatsappInboxEvent) return undefined;
    let cancelled = false;
    const previousUnread = messageUnread;
    (async () => {
      const count = await fetchMessageUnread();
      if (cancelled) return;
      if (
        whatsappNotifySound.isInboundWhatsAppEvent(whatsappInboxEvent) &&
        count > previousUnread
      ) {
        whatsappNotifySound.playInboundOnce(whatsappInboxEvent);
      }
    })();
    return () => {
      cancelled = true;
    };
    // messageUnread deliberately omitted: capture pre-event value once per seq.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappEnabled, whatsappInboxEvent, fetchMessageUnread]);

  useEffect(() => {
    if (!whatsappEnabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchMessageUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [whatsappEnabled, fetchMessageUnread]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axiosInstance.post(`/notifications/mark_as_read/`, {id: notificationId});
      setNotifications(notifications.map(notification => 
        notification.id === notificationId ? { ...notification, is_read: true } : notification
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      showNotification('Failed to mark notification as read', 'error');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axiosInstance.post(`/notifications/mark_all_read`);
      setNotifications(notifications.map(notification => ({ 
        ...notification, 
        is_read: true 
      })));
      showNotification('All notifications marked as read', 'success');
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      showNotification('Failed to mark all notifications as read', 'error');
    }
  };

  useEffect(() => {
    // Always set navbar to sticky for consistent white background
    setNavbarType("sticky");

    // Set transparent navbar to false to always show white background
    setTransparentNavbar(dispatch, false);
  }, [dispatch, fixedNavbar]);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setOpenMenu((prev) => (prev ? false : event.currentTarget));
  };

  const handleCloseMenu = () => setOpenMenu(false);

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleHelpClick = () => {
    navigate('/support-ticket');
  };

  const handleAvatarClick = (event) => {
    event.stopPropagation();
    setAvatarMenu((prev) => (prev ? null : event.currentTarget));
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenu(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleAvatarMenuClose();
  };

  const handleToggleUiVersion = async () => {
    handleAvatarMenuClose();
    try {
      await setUiVersion(isV2 ? UI_LEGACY : UI_V2);
    } catch (error) {
      console.error('Could not switch the UI version:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    handleAvatarMenuClose();
  };

  const iconsStyle = ({ palette: { dark, white, text } }) => ({
    color: darkMode ? text.main : light ? white.main : dark.main,
  });


const handleNotificationClick = async (notification) => {
  try {
    await handleMarkAsRead(notification.id);
    handleCloseMenu();

    const redirectPath = getNotificationRedirectPath(
      notification.parent_table,
      notification.parent_table_id,
    );
    if (!redirectPath) {
      showNotification('Unable to open the related record for this notification', 'warning');
      return;
    }

    navigate(redirectPath);
  } catch (error) {
    console.error("Error handling notification click:", error);
    showNotification('Failed to process notification', 'error');
  }
};

// Add this helper function to get appropriate icons for different types
const getIconForType = (type) => {
  const iconMap = {
    'lead': 'person_add',
    'leads': 'person_add',
    'deal': 'monetization_on',
    'deals': 'monetization_on',
    'account': 'business',
    'accounts': 'business',
    'case': 'cases',
    'cases': 'cases',
    'activity': 'event',
    'activities': 'event',
    'task': 'task',
    'tasks': 'task'
  };

  return iconMap[type] || 'article';
};

  const renderMenu = () => (
    <Paper
      id="notification-menu"
      elevation={8}
      sx={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        zIndex: 1400,
        width: { xs: 320, sm: 400 },
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: (theme) => theme.shadows[8],
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.3),
          },
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {/* // Update the header rendering */}
<NotificationHeader>
  <Box display="flex" alignItems="center" justifyContent="space-between">
    <Box display="flex" alignItems="center" gap={1}>
      <Icon sx={{ color: 'primary.main' }}>notifications</Icon>
      <Typography variant="subtitle1" fontWeight="600">
        Notifications
      </Typography>
    </Box>
    <Box display="flex" gap={1}>
      {notifications.length > 0 && (
        <Tooltip title="Mark all as read">
          <IconButton size="small" onClick={handleMarkAllAsRead}>
            <Icon fontSize="small">done_all</Icon>
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" onClick={handleCloseMenu}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>
  
  <Box display="flex" alignItems="center" justifyContent="space-between">
    <Typography variant="caption" color="text.secondary">
      {notifications.filter(n => !n.is_read).length} unread
    </Typography>
    {notifications.length > 0 && (
      <Typography variant="caption" color="text.secondary">
        {notifications.length} total
      </Typography>
    )}
  </Box>
</NotificationHeader>
  
      {loading ? (
        [...Array(3)].map((_, index) => (
          <StyledMenuItem key={index} disabled>
            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 1 }} />
          </StyledMenuItem>
        ))
      ) : notifications.length === 0 ? (
        <Box p={4} textAlign="center">
          <Icon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}>notifications_off</Icon>
          <Typography variant="body2" color="text.secondary">
            No new notifications
          </Typography>
        </Box>
      ) : (
        notifications.map((notification) => (
          <StyledMenuItem 
            key={notification.id}
            onClick={() => handleNotificationClick(notification)}
            sx={{
              backgroundColor: notification.is_read ? 'transparent' : (theme) => 
                theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.04),
              padding: '12px 16px',
            }}
          >
            <Box display="flex" gap={2} width="100%">
              <NotificationAvatar>
                {notification.owner?.name?.charAt(0) || '?'}
              </NotificationAvatar>
              
              <Box flex={1}>
                <NotificationTitle>
                  {notification.title}
                </NotificationTitle>
                
                <StyledNotificationText 
                  color="text.primary"
                  sx={{ 
                    fontSize: '0.825rem',
                    lineHeight: 1.5,
                  }}
                >
                  {notification.message}
                </StyledNotificationText>
  
                <NotificationMeta>
                  <MetaItem>
                    <Icon sx={{ fontSize: 16 }}>person</Icon>
                    {notification.owner?.name || ''}
                  </MetaItem>
                  
                  <Tooltip title={(() => {
                    const when = parseServerDateTime(notification.created_at);
                    return when ? format(when, 'PPpp') : '';
                  })()}>
                    <MetaItem sx={{ cursor: 'help' }}>
                      <Icon sx={{ fontSize: 16 }}>schedule</Icon>
                      {(() => {
                        const when = parseServerDateTime(notification.created_at);
                        return when
                          ? formatDistanceToNow(when, { addSuffix: true })
                          : '';
                      })()}
                    </MetaItem>
                  </Tooltip>
  
                  {!notification.is_read && (
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        ml: 'auto'
                      }} 
                    />
                  )}
                </NotificationMeta>
              </Box>
  
              {!notification.is_read && (
                <Box sx={{ alignSelf: 'flex-start' }}>
                  <Tooltip title="Mark as read">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: (theme) => 
                            alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <Icon fontSize="small">done</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </StyledMenuItem>
        ))
      )}
    </Paper>
  );

  return (
    <>
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini, align: "start" })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini, align: "end" })}>
            {/* Search Component — shrinks on narrow layouts so icons stay visible */}
            <MDBox
              pr={1}
              sx={{
                width: { xs: '100%', sm: '220px', md: '260px', lg: '300px' },
                minWidth: 0,
                flex: { xs: '1 1 auto', md: '0 1 auto' },
              }}
            >
              <SearchComponent />
            </MDBox>
            
            {/* Right side icons - all in one container for consistent alignment */}
            <MDBox 
              display="flex" 
              alignItems="center" 
              gap={0.5}
              color={light ? "white" : "inherit"}
              sx={{ flexShrink: 0 }}
            >
              {/* Theme toggle hidden — dark/light switch disabled for now
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                <DarkModeToggle iconOnly />
              </Box>
              */}

              {/* Zoom Controls */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {/* v2 renders at 100% with no body zoom, so there is nothing to adjust. */}
                {!isV2 && <ZoomControls />}
              </Box>

              {/* WhatsApp messages — only when the tenant holds the WHATSAPP add-on */}
              {whatsappEnabled && (
              <Tooltip title="Messages" placement="bottom">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={() => navigate('/messages')}
                >
                  <Badge badgeContent={messageUnread} color="error">
                    <ChatOutlinedIcon sx={iconsStyle} />
                  </Badge>
                </IconButton>
              </Tooltip>
              )}

              {/* Notifications — in-flow under the bell so CSS zoom cannot drift it */}
              <ClickAwayListener onClickAway={handleCloseMenu}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Tooltip title="Notifications" placement="bottom">
                    <IconButton
                      size="small"
                      disableRipple
                      color="inherit"
                      sx={navbarIconButton}
                      aria-controls={openMenu ? 'notification-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                      onClick={handleOpenMenu}
                    >
                      <Badge
                        badgeContent={notifications.filter(n => !n.is_read).length}
                        color="error"
                      >
                        <Icon sx={iconsStyle}>notifications</Icon>
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  {Boolean(openMenu) && renderMenu()}
                </Box>
              </ClickAwayListener>

              {/* Settings */}
              <Tooltip 
                title={user?.role !== 'system_admin' ? "Only system administrators can access settings" : "Settings"}
                placement="bottom"
              >
                <span>
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={{
                      ...navbarIconButton,
                      ...(user?.role !== 'system_admin' && {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        }
                      })
                    }}
                    onClick={handleSettingsClick}
                    disabled={user?.role !== 'system_admin'}
                  >
                    <Icon sx={iconsStyle}>settings</Icon>
                  </IconButton>
                </span>
              </Tooltip>

              {/* User Avatar — menu is in-flow under the icon so CSS zoom cannot drift it */}
              <ClickAwayListener onClickAway={handleAvatarMenuClose}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Tooltip
                    title={
                      [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
                      user?.username ||
                      'Profile'
                    }
                    placement="bottom"
                  >
                    <IconButton
                      size="small"
                      disableRipple
                      color="inherit"
                      onClick={handleAvatarClick}
                      aria-controls={avatarMenu ? 'profile-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={avatarMenu ? 'true' : undefined}
                      sx={{
                        p: 0.25,
                        ml: 0.5,
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    >
                      <MDUserAvatar
                        firstName={user?.first_name}
                        lastName={user?.last_name}
                        avatarUrl={user?.avatar}
                      />
                    </IconButton>
                  </Tooltip>
                  {Boolean(avatarMenu) && (
                    <Paper
                      id="profile-menu"
                      elevation={8}
                      sx={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        zIndex: 1400,
                        minWidth: 220,
                        py: 1,
                        px: 0.5,
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
                        overflow: 'hidden',
                        '& .MuiMenuItem-root': {
                          mx: 0.5,
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '8px',
                          minHeight: 40,
                        },
                        '& .MuiDivider-root': {
                          my: 0.75,
                          mx: 1,
                        },
                      }}
                    >
                      <MenuItem onClick={handleProfileClick}>
                        <Icon sx={{ mr: 1.25 }}>person</Icon>
                        Profile Settings
                      </MenuItem>
                      <MenuItem onClick={handleHelpClick}>
                        <HelpOutlineIcon sx={{ mr: 1.25 }} />
                        Contact Support
                      </MenuItem>
                      {uiV2Enabled && (
                        <MenuItem onClick={handleToggleUiVersion}>
                          <Icon sx={{ mr: 1.25 }}>{isV2 ? 'undo' : 'auto_awesome'}</Icon>
                          {isV2 ? 'Switch back to classic' : 'Try the new look'}
                        </MenuItem>
                      )}
                      <Divider />
                      <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
                        <Icon sx={{ mr: 1.25 }}>logout</Icon>
                        Sign Out
                      </MenuItem>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>

    {/* // Customize the Snackbar and Alert styling */}
<Snackbar
  open={snackbar.open}
  autoHideDuration={4000}
  onClose={handleSnackbarClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} // Change position
  sx={{
    '& .MuiSnackbar-root': {
      top: '24px', // Add some top margin
    }
  }}
>
  <Alert
    onClose={handleSnackbarClose}
    severity={snackbar.severity}
    variant="filled"
    sx={{
      minWidth: '300px',
      boxShadow: (theme) => theme.shadows[3],
      '& .MuiAlert-icon': {
        fontSize: '1.25rem'
      },
      '& .MuiAlert-message': {
        fontSize: '0.875rem',
        fontWeight: 500
      },
      '& .MuiAlert-action': {
        paddingTop: 0
      }
    }}
  >
    {snackbar.message}
  </Alert>
</Snackbar>
</>
  );
}

// Setting default values for the props of DashboardNavbar
ClassicDashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
ClassicDashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

/**
 * Under UI v2 the global header (shell-v2/HeaderV2) owns search,
 * notifications, settings and the avatar, so every page's navbar becomes a
 * breadcrumb row. The classic navbar is not mounted at all there, so its
 * notification and WhatsApp fetches do not run twice.
 */
function DashboardNavbar(props) {
  const { isV2 } = useUiVersion();
  if (isV2) return <PageBreadcrumbs />;
  return <ClassicDashboardNavbar {...props} />;
}

DashboardNavbar.defaultProps = ClassicDashboardNavbar.defaultProps;
DashboardNavbar.propTypes = ClassicDashboardNavbar.propTypes;

export default DashboardNavbar;
